/**
 * @file
 * Image Hotspots behavior — list and SVG marker synchronization.
 */

((Drupal, once) => {
  ('use strict');

  /**
   * Initializes one image hotspots component instance.
   *
   * @param root
   *   The component root element.
   */
  function initImageHotspots(root: HTMLElement): void {
    const image = root.querySelector<HTMLImageElement>('[data-hotspot-image]');
    const canvas = root.querySelector<SVGElement>('[data-hotspot-canvas]');
    const pointers = Array.from(root.querySelectorAll<HTMLButtonElement>('.image-hotspots__pointer'));
    const areas = Array.from(root.querySelectorAll<HTMLElement>('.image-hotspots__area'));

    if (!image || !canvas) {
      return;
    }

    const syncViewBox = (): void => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        canvas.setAttribute('viewBox', `0 0 ${image.naturalWidth} ${image.naturalHeight}`);
      }
    };

    if (image.complete) {
      syncViewBox();
    } else {
      image.addEventListener('load', syncViewBox, { once: true });
    }

    const getArea = (mapId: string): HTMLElement | null => {
      return root.querySelector<HTMLElement>(`#${root.id}-area-${mapId}`);
    };

    const activatePointer = (mapId: string): void => {
      const pointer = root.querySelector<HTMLButtonElement>(`#${root.id}-pointer-${mapId}`);
      pointer?.classList.add('is-active', 'is-clicked');
      getArea(mapId)?.classList.add('is-active', 'is-clicked');
    };

    const deactivatePointer = (mapId: string): void => {
      const pointer = root.querySelector<HTMLButtonElement>(`#${root.id}-pointer-${mapId}`);
      pointer?.classList.remove('is-active', 'is-clicked');
      getArea(mapId)?.classList.remove('is-active', 'is-clicked');
    };

    const highlightPointer = (mapId: string): void => {
      root.querySelector<HTMLButtonElement>(`#${root.id}-pointer-${mapId}`)?.classList.add('is-active');
      getArea(mapId)?.classList.add('is-active');
    };

    const unhighlightPointer = (mapId: string): void => {
      const pointer = root.querySelector<HTMLButtonElement>(`#${root.id}-pointer-${mapId}`);
      const area = getArea(mapId);
      if (!pointer?.classList.contains('is-clicked')) {
        pointer?.classList.remove('is-active');
      }
      if (!area?.classList.contains('is-clicked')) {
        area?.classList.remove('is-active');
      }
    };

    pointers.forEach((pointer) => {
      const mapId = pointer.dataset.mapid;
      if (!mapId) {
        return;
      }

      pointer.addEventListener('click', () => {
        if (pointer.classList.contains('is-clicked')) {
          deactivatePointer(mapId);
        } else {
          activatePointer(mapId);
        }
      });

      pointer.addEventListener('mouseenter', () => highlightPointer(mapId));
      pointer.addEventListener('mouseleave', () => unhighlightPointer(mapId));
    });

    areas.forEach((area) => {
      const listId = area.dataset.listid;
      if (!listId) {
        return;
      }

      area.addEventListener('click', () => {
        if (area.classList.contains('is-clicked')) {
          area.classList.remove('is-clicked');
          deactivatePointer(listId);
        } else {
          area.classList.add('is-clicked');
          activatePointer(listId);
        }
      });

      area.addEventListener('mouseenter', () => highlightPointer(listId));
      area.addEventListener('mouseleave', () => {
        if (!area.classList.contains('is-clicked')) {
          unhighlightPointer(listId);
        }
      });
    });
  }

  Drupal.behaviors.tpImageHotspots = {
    attach(context: Document | HTMLElement) {
      once('tp-image-hotspots', '[data-image-hotspots]', context).forEach((element) => {
        initImageHotspots(element as HTMLElement);
      });
    },
  };
})(Drupal, once);
